// Browserens forbindelse til et rum: WebSocket med genforbindelse (0,5/1/2/4 s), token i
// localStorage, hjerteslag, ops uden huller og hash-tjek. Kender intet til spillet ud over
// protokollen; src/net/online.ts kobler det til Game og UI.

import { hash } from '../sim';
import type { ActionKind, Intent, State } from '../sim';
import { BUILD, PROTO, parseServer, toWire } from './protocol';
import type { ClientMsg, ErrorCode, Op, Pause, PingAt, PingKind, PingReply, RoomView, Seat, ServerMsg, WorldP } from './protocol';

export type NetStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface RoomEvents {
  status(s: NetStatus): void;
  welcome(you: Seat, room: RoomView, lastSeq: number): void;
  room(room: RoomView): void;
  snap(state: State, n: number, paused: Pause, speed: 1 | 2, ready?: Seat[]): void;
  /** Nye ops i rækkefølge, uden huller og dubletter. */
  ops(ops: Op[]): void;
  ack(seq: number, ok: boolean, reason?: string): void;
  ping(m: Extract<ServerMsg, { t: 'ping' }>): void;
  cursor(from: Seat, p: WorldP | null): void;
  ctl(paused: Pause, speed: 1 | 2, by?: Seat, ready?: Seat[], go?: number): void;
  /** Et nej, der ikke lukker forbindelsen (fx not_host). */
  refused?(code: ErrorCode): void;
  /** Endelig fejl: forbindelsen prøver ikke igen. */
  fatal(code: ErrorCode | 'replaced'): void;
}

const BACKOFF = [500, 1000, 2000, 4000];
const HB_MS = 5_000;
/** Intet hørt fra rummet så længe: forbindelsen er død, selv om browseren ikke har opdaget det. */
const SILENT_MS = 12_000;
const STORE = 'fs-room';

interface Stored {
  room: string;
  playerToken: string;
}

function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORE);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Stored>;
    return typeof v.room === 'string' && typeof v.playerToken === 'string' ? { room: v.room, playerToken: v.playerToken } : null;
  } catch {
    return null;
  }
}

function writeStored(v: Stored | null): void {
  try {
    if (v) localStorage.setItem(STORE, JSON.stringify(v));
    else localStorage.removeItem(STORE);
  } catch {
    // Privat vindue eller blokeret lager: vi klarer os uden (genforbindelse virker stadig i fanen).
  }
}

/** Rummet, browseren sidst var med i (til "Tilbage til rummet"). */
export function lastRoom(): string | null {
  return readStored()?.room ?? null;
}

export class RoomClient {
  status: NetStatus = 'connecting';
  seat: Seat | null = null;
  /** Næste op-indeks, vi venter på fra rummet. */
  n = 0;
  desyncs = 0;
  /** Målt tid fra intent til ack (ms), til e2e og fejlfinding. */
  rtt: number[] = [];
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private tries = 0;
  private timer = 0;
  private hb = 0;
  private heard = 0;
  private welcomed = false;
  private closed = false;
  private haveSnap = false;
  private checks = new Map<number, string>();
  private sentAt = new Map<number, number>();
  private syncing = false;
  /** Hash-tjek mod rummet, der passede (til e2e og fejlfinding). */
  checked = 0;
  /** Hash-tjek modtaget fra rummet (til fejlsøgning: checked bør følge med). */
  hashes = 0;
  lastCheck: { n: number; hash: string } | null = null;

  constructor(
    readonly code: string,
    private ev: RoomEvents,
    private base = location.origin.replace(/^http/, 'ws'),
  ) {
    const s = readStored();
    this.token = s && s.room === code ? s.playerToken : null;
    this.connect();
    document.addEventListener('visibilitychange', this.onVisible);
    window.addEventListener('online', this.onOnline);
  }

  private setStatus(s: NetStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.ev.status(s);
  }

  private connect(): void {
    window.clearTimeout(this.timer);
    if (this.closed) return;
    const re = this.token ? '?re=1' : '';
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${this.base}/ws/room/${this.code}${re}`);
    } catch {
      this.retry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      const m: ClientMsg = { t: 'hello', proto: PROTO, build: BUILD, room: this.code };
      if (this.token) m.token = this.token;
      ws.send(JSON.stringify(m));
      this.heard = performance.now();
      window.clearInterval(this.hb);
      this.hb = window.setInterval(() => this.beat(), HB_MS);
    };
    ws.onmessage = (e) => {
      if (ws !== this.ws) return;
      this.heard = performance.now();
      if (e.data === 'pong') return;
      const m = parseServer(e.data);
      if (m) this.handle(m);
    };
    ws.onclose = (e) => {
      if (ws !== this.ws) return;
      window.clearInterval(this.hb);
      this.ws = null;
      if (e.code === 4001) return this.fail('replaced');
      this.retry();
    };
    ws.onerror = () => {
      // onclose følger altid efter.
    };
  }

  private beat(): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (performance.now() - this.heard > SILENT_MS) {
      // Halvdød forbindelse (fx efter dvale på telefonen): luk og prøv igen.
      this.drop();
      return;
    }
    try {
      ws.send('ping');
    } catch {
      this.drop();
    }
  }

  /** Smid forbindelsen og prøv igen med det samme. */
  private drop(): void {
    const ws = this.ws;
    this.ws = null;
    window.clearInterval(this.hb);
    try {
      ws?.close();
    } catch {
      // Ligegyldigt.
    }
    this.retry(0);
  }

  private retry(delay?: number): void {
    if (this.closed) return;
    this.setStatus(this.welcomed ? 'reconnecting' : 'connecting');
    const d = delay ?? BACKOFF[Math.min(this.tries, BACKOFF.length - 1)] ?? 4000;
    this.tries++;
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.connect(), d);
  }

  private fail(code: ErrorCode | 'replaced'): void {
    this.closed = true;
    window.clearTimeout(this.timer);
    window.clearInterval(this.hb);
    this.setStatus('closed');
    this.ev.fatal(code);
  }

  private handle(m: ServerMsg): void {
    switch (m.t) {
      case 'welcome':
        this.seat = m.you;
        this.token = m.token;
        this.tries = 0;
        this.welcomed = true;
        writeStored({ room: this.code, playerToken: m.token });
        this.setStatus('open');
        this.ev.welcome(m.you, m.room, m.lastSeq);
        break;
      case 'seat':
        // Rollebyt i rummet: samme token, ny plads.
        this.seat = m.you;
        this.ev.welcome(m.you, m.room, m.lastSeq);
        break;
      case 'room':
        this.ev.room(m.room);
        break;
      case 'snap':
        this.n = m.n;
        this.haveSnap = true;
        this.syncing = false;
        this.checks.clear();
        this.ev.snap(m.state, m.n, m.paused, m.speed, m.ready);
        break;
      case 'ops': {
        if (!this.haveSnap) return;
        if (m.n > this.n) {
          // Et hul i rækken: bed om et snapshot (én gang ad gangen).
          this.sync();
          return;
        }
        const skip = this.n - m.n;
        if (skip >= m.ops.length) return;
        const fresh = m.ops.slice(skip);
        this.n += fresh.length;
        if (m.h) {
          this.checks.set(m.h[0], m.h[1]);
          this.hashes++;
        }
        this.ev.ops(fresh);
        break;
      }
      case 'ack': {
        const at = this.sentAt.get(m.seq);
        if (at !== undefined) {
          this.rtt.push(Math.round(performance.now() - at));
          if (this.rtt.length > 50) this.rtt.shift();
          this.sentAt.delete(m.seq);
        }
        this.ev.ack(m.seq, m.ok, m.reason);
        break;
      }
      case 'ping':
        this.ev.ping(m);
        break;
      case 'cursor':
        this.ev.cursor(m.from, m.p);
        break;
      case 'ctl':
        this.ev.ctl(m.paused, m.speed, m.by, m.ready, m.go);
        break;
      case 'error':
        if (m.code === 'gone' && this.token && !this.welcomed) {
          // Gammelt token til et rum, der er gået videre: prøv som ny spiller.
          this.token = null;
          writeStored(null);
          this.retry(0);
          return;
        }
        if (m.code === 'not_host') {
          this.ev.refused?.(m.code);
          return;
        }
        if (m.code === 'rate' || m.code === 'bad') {
          if (m.code === 'rate') this.retry(4000);
          return;
        }
        this.fail(m.code);
        break;
    }
  }

  /** Kaldes af Game efter hvert afspillet op: passer vores hash med rummets? */
  applied(n: number, state: State): void {
    const want = this.checks.get(n);
    if (want === undefined) return;
    this.checks.delete(n);
    if (hash(state) !== want) {
      this.desyncs++;
      this.sync();
      return;
    }
    this.checked++;
    this.lastCheck = { n, hash: want };
  }

  send(m: ClientMsg): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN || !this.welcomed) return false;
    try {
      ws.send(JSON.stringify(m));
      return true;
    } catch {
      return false;
    }
  }

  intent(i: Intent): boolean {
    const [, seq, kind, target, opt] = toWire(i);
    const m: ClientMsg = { t: 'intent', seq, kind };
    if (target !== undefined && target !== '') m.target = target;
    if (opt !== undefined) m.opt = opt;
    this.sentAt.set(seq, performance.now());
    return this.send(m);
  }

  ping(kind: PingKind, at: PingAt, reply?: PingReply, to?: number, act?: ActionKind): void {
    const m: ClientMsg = { t: 'ping', kind, at };
    if (reply) m.reply = reply;
    if (to !== undefined) m.to = to;
    if (act !== undefined) m.act = act;
    this.send(m);
  }

  cursor(p: WorldP | null): void {
    this.send({ t: 'cursor', p });
  }

  sync(): void {
    if (this.syncing) return;
    this.syncing = true;
    this.haveSnap = false;
    if (!this.send({ t: 'sync' })) {
      // Ingen forbindelse: velkomsten efter genforbindelse har et snapshot med.
      this.syncing = false;
      return;
    }
    // Kommer snapshottet ikke, spørger vi igen.
    window.setTimeout(() => {
      if (!this.syncing) return;
      this.syncing = false;
      if (!this.haveSnap) this.sync();
    }, 1500);
  }

  /** Forlad rummet for godt (sædet bliver ledigt med det samme). */
  leave(): void {
    this.send({ t: 'bye' });
    writeStored(null);
    this.dispose();
  }

  /** Luk forbindelsen uden at opgive sædet (fx ved navigation væk). */
  dispose(): void {
    this.closed = true;
    window.clearTimeout(this.timer);
    window.clearInterval(this.hb);
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('online', this.onOnline);
    const ws = this.ws;
    this.ws = null;
    try {
      ws?.close(1000, 'bye');
    } catch {
      // Ligegyldigt.
    }
  }

  private onVisible = (): void => {
    if (document.hidden || this.closed) return;
    // Tilbage i forgrunden: er forbindelsen død, så prøv straks; ellers er alt godt.
    if (!this.ws || this.ws.readyState > WebSocket.OPEN || performance.now() - this.heard > SILENT_MS) this.drop();
  };

  private onOnline = (): void => {
    if (!this.closed && !this.ws) this.retry(0);
  };
}
